import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { errorMessage } from './errors'
import { http } from './api'
import type {
  AddRatePeriodInput,
  CategoryInput,
  CategoryView,
  ClientDetail,
  ClientListItem,
  ClientMemberView,
  ClientWriteInput,
  CreateClientInput,
  DistributionPreviewInput,
  DistributionPreviewResult,
  ProjectInput,
  ProjectView,
  RateIndexationInput,
  RateIndexationPreview,
} from './clients-types'

export interface ClientsApi {
  list(search?: string): Promise<ClientListItem[]>
  get(id: string): Promise<ClientDetail>
  create(input: CreateClientInput): Promise<ClientDetail>
  update(id: string, input: ClientWriteInput): Promise<ClientDetail>
  createProject(id: string, input: ProjectInput): Promise<ProjectView>
  updateProject(id: string, projectId: string, input: ProjectInput): Promise<ProjectView>
  deleteProject(id: string, projectId: string): Promise<void>
  updateMembers(id: string, personIds: string[]): Promise<{ members: ClientMemberView[] }>
  createCategory(id: string, input: CategoryInput): Promise<CategoryView>
  updateCategory(id: string, categoryId: string, input: CategoryInput): Promise<CategoryView>
  deleteCategory(id: string, categoryId: string): Promise<void>
  addRatePeriod(id: string, input: AddRatePeriodInput): Promise<ClientDetail>
  rateIndexationPreview(id: string, input: RateIndexationInput): Promise<RateIndexationPreview>
  rateIndexationApply(id: string, input: RateIndexationInput): Promise<ClientDetail>
  previewDistributionScript(id: string, input: DistributionPreviewInput): Promise<DistributionPreviewResult>
}

class RealClientsApi implements ClientsApi {
  list(search?: string) {
    return http.get<ClientListItem[]>('/api/clients', { search })
  }
  get(id: string) {
    return http.get<ClientDetail>(`/api/clients/${id}`)
  }
  create(input: CreateClientInput) {
    return http.post<ClientDetail>('/api/clients', input)
  }
  update(id: string, input: ClientWriteInput) {
    return http.patch<ClientDetail>(`/api/clients/${id}`, input)
  }
  createProject(id: string, input: ProjectInput) {
    return http.post<ProjectView>(`/api/clients/${id}/projects`, input)
  }
  updateProject(id: string, projectId: string, input: ProjectInput) {
    return http.patch<ProjectView>(`/api/clients/${id}/projects/${projectId}`, input)
  }
  deleteProject(id: string, projectId: string) {
    return http.del<void>(`/api/clients/${id}/projects/${projectId}`)
  }
  updateMembers(id: string, personIds: string[]) {
    return http.put<{ members: ClientMemberView[] }>(`/api/clients/${id}/members`, { personIds })
  }
  createCategory(id: string, input: CategoryInput) {
    return http.post<CategoryView>(`/api/clients/${id}/categories`, input)
  }
  updateCategory(id: string, categoryId: string, input: CategoryInput) {
    return http.patch<CategoryView>(`/api/clients/${id}/categories/${categoryId}`, input)
  }
  deleteCategory(id: string, categoryId: string) {
    return http.del<void>(`/api/clients/${id}/categories/${categoryId}`)
  }
  addRatePeriod(id: string, input: AddRatePeriodInput) {
    return http.post<ClientDetail>(`/api/clients/${id}/rates`, input)
  }
  rateIndexationPreview(id: string, input: RateIndexationInput) {
    return http.post<RateIndexationPreview>(`/api/clients/${id}/rates/indexation/preview`, input)
  }
  rateIndexationApply(id: string, input: RateIndexationInput) {
    return http.post<ClientDetail>(`/api/clients/${id}/rates/indexation/apply`, input)
  }
  previewDistributionScript(id: string, input: DistributionPreviewInput) {
    return http.post<DistributionPreviewResult>(`/api/clients/${id}/distribution-script/preview`, input)
  }
}

export const clientsApi: ClientsApi = new RealClientsApi()

export const clientKeys = {
  list: (search?: string) => ['clients', 'list', search ?? ''] as const,
  detail: (id: string) => ['clients', 'detail', id] as const,
}

export function useClientList(search?: string, enabled = true) {
  return useQuery({ queryKey: clientKeys.list(search), queryFn: () => clientsApi.list(search), enabled })
}

export function useClient(id: string) {
  return useQuery({ queryKey: clientKeys.detail(id), queryFn: () => clientsApi.get(id) })
}

export function useCreateClient() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateClientInput) => clientsApi.create(input),
    onSuccess: (detail) => {
      queryClient.setQueryData(clientKeys.detail(detail.id), detail)
      queryClient.invalidateQueries({ queryKey: ['clients', 'list'] })
      toast.success('Klant aangemaakt')
    },
    onError: (e) => toast.error(errorMessage(e, 'Aanmaken mislukt')),
  })
}

export function useUpdateClient(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: ClientWriteInput) => clientsApi.update(id, input),
    onSuccess: (detail) => {
      queryClient.setQueryData(clientKeys.detail(id), detail)
      queryClient.invalidateQueries({ queryKey: ['clients', 'list'] })
      toast.success('Klant opgeslagen')
    },
    onError: (e) => toast.error(errorMessage(e, 'Opslaan mislukt')),
  })
}

function useDetailRefetch(id: string) {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({ queryKey: clientKeys.detail(id) })
    queryClient.invalidateQueries({ queryKey: ['clients', 'list'] })
  }
}

export function useSaveProject(id: string) {
  const refetch = useDetailRefetch(id)
  return useMutation({
    mutationFn: (vars: { projectId?: string; input: ProjectInput }) =>
      vars.projectId ? clientsApi.updateProject(id, vars.projectId, vars.input) : clientsApi.createProject(id, vars.input),
    onSuccess: (_data, vars) => {
      refetch()
      toast.success(vars.projectId ? 'Project opgeslagen' : 'Project toegevoegd')
    },
    onError: (e) => toast.error(errorMessage(e, 'Opslaan mislukt')),
  })
}

export function useDeleteProject(id: string) {
  const refetch = useDetailRefetch(id)
  return useMutation({
    mutationFn: (projectId: string) => clientsApi.deleteProject(id, projectId),
    onSuccess: () => {
      refetch()
      toast.success('Project verwijderd')
    },
    onError: (e) => toast.error(errorMessage(e, 'Verwijderen mislukt')),
  })
}

export function useSaveClientMembers(id: string) {
  const refetch = useDetailRefetch(id)
  return useMutation({
    mutationFn: (personIds: string[]) => clientsApi.updateMembers(id, personIds),
    onSuccess: () => {
      refetch()
      toast.success('Toegang bijgewerkt')
    },
    onError: (e) => toast.error(errorMessage(e, 'Opslaan mislukt')),
  })
}

export function useSaveCategory(id: string) {
  const refetch = useDetailRefetch(id)
  return useMutation({
    mutationFn: (vars: { categoryId?: string; input: CategoryInput }) =>
      vars.categoryId ? clientsApi.updateCategory(id, vars.categoryId, vars.input) : clientsApi.createCategory(id, vars.input),
    onSuccess: (_data, vars) => {
      refetch()
      toast.success(vars.categoryId ? 'Categorie opgeslagen' : 'Categorie toegevoegd')
    },
    onError: (e) => toast.error(errorMessage(e, 'Opslaan mislukt')),
  })
}

export function useDeleteCategory(id: string) {
  const refetch = useDetailRefetch(id)
  return useMutation({
    mutationFn: (categoryId: string) => clientsApi.deleteCategory(id, categoryId),
    onSuccess: () => {
      refetch()
      toast.success('Categorie verwijderd')
    },
    onError: (e) => toast.error(errorMessage(e, 'Verwijderen mislukt')),
  })
}

export function useAddRatePeriod(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: AddRatePeriodInput) => clientsApi.addRatePeriod(id, input),
    onSuccess: (detail) => {
      queryClient.setQueryData(clientKeys.detail(id), detail)
      toast.success('Nieuwe tariefperiode toegevoegd')
    },
    onError: (e) => toast.error(errorMessage(e, 'Opslaan mislukt')),
  })
}

export function useRateIndexationPreview(id: string) {
  return useMutation({
    mutationFn: (input: RateIndexationInput) => clientsApi.rateIndexationPreview(id, input),
    onError: (e) => toast.error(errorMessage(e, 'Preview mislukt')),
  })
}

export function useRateIndexationApply(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: RateIndexationInput) => clientsApi.rateIndexationApply(id, input),
    onSuccess: (detail) => {
      queryClient.setQueryData(clientKeys.detail(id), detail)
      toast.success('Tariefindexatie toegepast')
    },
    onError: (e) => toast.error(errorMessage(e, 'Toepassen mislukt')),
  })
}

export function usePreviewDistributionScript(id: string) {
  return useMutation({
    mutationFn: (input: DistributionPreviewInput) => clientsApi.previewDistributionScript(id, input),
    onError: (e) => toast.error(errorMessage(e, 'Proefberekening mislukt')),
  })
}
